import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GPT_LANDING_PATH } from "@juisci/gpt-handoff";
import { displayToast } from "../../../components/app/AppToast";
import { loginWithApple, loginWithGoogle } from "../../../services/firebase";
import { classifyEmailForAuth } from "../checkEmailOutcome";
import { isPublicBrowserPath } from "../publicBrowserPaths";
import {
  desktopCodeRequestStore,
  type DesktopCodeRequest,
} from "../stores/desktopCodeRequestStore";
import { useSignupInFlight } from "../stores/signupInFlightStore";
import { getPostLoginRedirectPath } from "../../app/utils/postLoginRedirect";
import { gaEventAuthMethodSelected } from "../../../tools/analytics/domains/onboarding.analytics";
import { gaEventStartSignUp } from "../../../tools/analytics/domains/auth.analytics";

/**
 * The desktop front door, first half: the address, on `/signup` or `/login`
 * (Figma "D7.inscription.welcome", "D7.connexion").
 *
 * Each door answers both audiences. `check-email` decides whether the code that
 * follows is a login or a signup, so the address, not the door, decides. It then hands the request to the auth gate
 * (`desktopCodeRequestStore`) and shows the app behind that modal: the page
 * the visitor was bounced off, or JuisciGPT.
 *
 * Where the sign-in lands is the memorized page (`loginAndRedirect`): the
 * logged-out guard memorizes the page it bounced the visitor off, and the
 * logged-out JuisciGPT page memorizes itself before it sends them here.
 */
export function useDesktopLogin({
  surface,
  prefilledEmail,
}: {
  surface: DesktopCodeRequest["surface"];
  prefilledEmail: string | null;
}): {
  email: string;
  setEmail: (email: string) => void;
  isBusy: boolean;
  onEmailSubmit: () => void;
  onProvider: (provider: "apple" | "google") => void;
} {
  const history = useHistory();
  const { t } = useTranslation();
  const [email, setEmail] = useState(
    prefilledEmail === null ? "" : prefilledEmail
  );
  const [isCheckingEmail, setCheckingEmail] = useState(false);
  // An Apple or Google popup is a sign-in in progress too. A second click would
  // open another popup, and Firebase cancels the first one: that flow then signs
  // out and clears the in-flight flag while the second is still running.
  const signupInFlight = useSignupInFlight();

  // Arriving at the door abandons any code asked for before: the visitor came
  // Back from the modal, or followed "Create an account". Without this, the
  // request stayed in memory, and Back once more onto the page it stood over
  // (the logged-out JuisciGPT page, typically) put the modal up again there.
  useEffect(() => {
    desktopCodeRequestStore.set(null);
  }, []);

  function requestCode(intent: DesktopCodeRequest["intent"]): void {
    // The app the visitor is signing into, behind the code. A memorized public
    // page has no gate over it, so the code would have nowhere to be typed.
    // Nor do the settings: logged out, their route sends to `/login`, which
    // drops the request, and the sign-in looped back to the same door. The
    // sign-in still lands on the memorized page, settings included.
    const memorized = getPostLoginRedirectPath();
    const behind =
      memorized === "" ||
      isPublicBrowserPath(memorized) ||
      memorized.startsWith("/settings")
        ? GPT_LANDING_PATH
        : memorized;
    desktopCodeRequestStore.set({
      email: email.trim(),
      behindPathname: new URL(behind, globalThis.location.origin).pathname,
      intent,
      surface,
    });
    history.push(behind);
  }

  function onEmailSubmit(): void {
    if (isCheckingEmail) return;
    gaEventAuthMethodSelected({
      method: "email",
      intent: "signup",
      surface,
    });

    setCheckingEmail(true);
    void (async () => {
      try {
        const outcome = await classifyEmailForAuth(email);
        switch (outcome.kind) {
          case "invalid": {
            displayToast(t("Please input a valid email address."), "warning");
            return;
          }
          case "failed": {
            displayToast(t("Something went wrong. Please try again."), "error");
            return;
          }
          case "existing": {
            requestCode("login");
            return;
          }
          case "new": {
            // The code screen IS the start of signup here, as it is on mobile:
            // fired now so the funnel's denominator includes everyone who
            // abandons on it.
            gaEventStartSignUp({
              account_type: "email",
              origin: "direct download",
            });
            requestCode("signup");
            return;
          }
          default: {
            throw new Error(outcome satisfies never);
          }
        }
      } finally {
        setCheckingEmail(false);
      }
    })();
  }

  function onProvider(provider: "apple" | "google"): void {
    gaEventAuthMethodSelected({
      method: provider,
      intent: "signup",
      surface,
    });
    const context = { surface, intent: "signup", returnTo: null } as const;
    switch (provider) {
      case "apple": {
        void loginWithApple(context);
        return;
      }
      case "google": {
        void loginWithGoogle(context);
        return;
      }
      default: {
        throw new Error(`Unknown provider: ${provider satisfies never}`);
      }
    }
  }

  return {
    email,
    setEmail,
    isBusy: signupInFlight || isCheckingEmail,
    onEmailSubmit,
    onProvider,
  };
}
